import React, { useContext, useEffect, useRef, useState } from "react";
import { Dot } from "lucide-react";
import { GlobalContext } from "../Context/GlobalContext";

function debounce(fn, wait = 120) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
}

const Notification = () => {
  const {
    notification,
    getNotificationList,
    readNotifications,
    translatedLabels,
  } = useContext(GlobalContext);
  const screenLabels = translatedLabels?.common;

  const [expandedIndex, setExpandedIndex] = useState(null);

  const [meta, setMeta] = useState(() =>
    notification.map(() => ({ isOverflow: false, truncated: "" })),
  );

  const bodyRefs = useRef([]);

  useEffect(() => {
    getNotificationList();
  }, [getNotificationList]);

  useEffect(() => {
    if (notification.some((n) => n.is_read === 0)) {
      readNotifications();
    }
  }, [notification, readNotifications]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const computeAll = () => {
      const newMeta = notification.map(() => ({
        isOverflow: false,
        truncated: "",
      }));

      notification.forEach((n, idx) => {
        const bodyEl = bodyRefs.current[idx];
        if (!bodyEl) return;

        const fullText = n.message;
        const cs = window.getComputedStyle(bodyEl);

        let lineHeightPx = parseFloat(cs.lineHeight);
        if (isNaN(lineHeightPx)) {
          const fontSizePx = parseFloat(cs.fontSize) || 14;
          lineHeightPx = fontSizePx * 1.25;
        }
        const maxHeight = lineHeightPx * 2;

        const width = bodyEl.clientWidth;

        const m = document.createElement("div");
        Object.assign(m.style, {
          position: "absolute",
          left: "-9999px",
          top: "0",
          visibility: "hidden",
          width: `${width}px`,
          fontSize: cs.fontSize,
          fontFamily: cs.fontFamily,
          fontWeight: cs.fontWeight,
          lineHeight: cs.lineHeight,
          whiteSpace: "normal",
          wordWrap: "break-word",
          padding: "0",
          margin: "0",
          boxSizing: "border-box",
        });
        document.body.appendChild(m); // ✅ FIX

        m.textContent = fullText;
        if (m.scrollHeight <= maxHeight) {
          newMeta[idx] = { isOverflow: false, truncated: "" };
          document.body.removeChild(m); // ✅ FIX
          return;
        }

        const moreIndicator = "… more";
        let left = 0;
        let right = fullText.length;
        let fit = 0;

        while (left <= right) {
          const mid = Math.floor((left + right) / 2);
          m.textContent = "";
          m.appendChild(document.createTextNode(fullText.slice(0, mid)));
          const span = document.createElement("span");
          span.textContent = moreIndicator;
          m.appendChild(span);

          if (m.scrollHeight <= maxHeight) {
            fit = mid;
            left = mid + 1;
          } else {
            right = mid - 1;
          }
        }

        let truncated = fullText.slice(0, fit).trim();

        if (fit < fullText.length) {
          const lastSpace = truncated.lastIndexOf(" ");
          if (lastSpace > 0) truncated = truncated.slice(0, lastSpace);
        }

        newMeta[idx] = { isOverflow: true, truncated };
        document.body.removeChild(m); // ✅ FIX
      });

      setMeta(newMeta);
    };

    computeAll();

    const onResize = debounce(() => computeAll(), 120);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [notification.length, notification]);

  const toggle = (i) => {
    setExpandedIndex((prev) => (prev === i ? null : i));
  };

  return (
    <div className="content-part bg-white">
      {notification.length > 0 ? (
        notification.map((n, i) => {
          const isExpanded = expandedIndex === i;
          const m = meta[i] || { isOverflow: false, truncated: "" };

          return (
            <div
              key={i}
              className="card border-0 shadow-none"
              style={{
                background: n.is_read
                  ? "rgb(169 169 169 / 11%)"
                  : "rgb(234 245 255 / 57%)",
                marginBottom: 8,
              }}
            >
              <div className="card-body" style={{ display: "flex", gap: 12 }}>
                {!n.is_read && (
                  <div className="read-icon" style={{ flex: "0 0 40px" }}>
                    <Dot size={40} color="#ff6262" />
                  </div>
                )}

                <div style={{ flex: 1 }}>
                  <div className="mb-1 d-flex align-items-center justify-content-between">
                    <h6 className="mb-0" style={{ margin: 0 }}>
                      {n.title}
                    </h6>
                    <small className="text-muted" style={{ fontSize: 12 }}>
                      {n.delivered_at}
                    </small>
                  </div>

                  <p
                    ref={(el) => (bodyRefs.current[i] = el)}
                    className="notification-body"
                    style={{
                      maxHeight: isExpanded ? "500px" : "3.0em",
                    }}
                  >
                    {isExpanded ? (
                      <>
                        {n.message}{" "}
                        {m.isOverflow && (
                          <span
                            className="text-decoration-underline ms-1"
                            role="button"
                            tabIndex={0}
                            onClick={() => toggle(i)}
                            onKeyDown={(e) => e.key === "Enter" && toggle(i)}
                          >
                            Show less
                          </span>
                        )}
                      </>
                    ) : m.isOverflow ? (
                      <>
                        {m.truncated}
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={() => toggle(i)}
                          onKeyDown={(e) => e.key === "Enter" && toggle(i)}
                          className="text-decoration-underline ms-1"
                        >
                          more
                        </span>
                      </>
                    ) : (
                      n.message
                    )}
                  </p>
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="text-center mt-3">
          <p>
            {screenLabels?.no_notification_available?.label_value ||
              "No Notification Available"}
          </p>
        </div>
      )}
    </div>
  );
};

export default Notification;
